import { useState } from 'react'
import { usuarios } from 'service/api'
import { toast } from 'sonner'
import FilterModelPro, { GridFilterModelPro } from '../utils/filterModelPro'
import { GridColDefPro } from '../utils/types'

type TFilterPreset = {
    id: number
    nome: string
    items: GridFilterModelPro['items']
}

export default function useFilterPresets({
    columns,
}: {
    columns: GridColDefPro[]
}) {
    const [filtros, setFiltros] = useState<TFilterPreset[]>()
    const [isLoading, setLoading] = useState(false)

    if (!filtros) {
        const filtrosStorage = sessionStorage.getItem('preset-filtros')
        try {
            if (!filtrosStorage || filtrosStorage === 'undefined')
                throw new Error('Sem filtros')
            else setFiltros(JSON.parse(filtrosStorage))
        } catch (_error) {
            usuarios.filtros.get().then(res => {
                const response = res.map(f => {
                    const filtro = FilterModelPro.fromUrlParams(
                        JSON.stringify(f.items),
                    )

                    return {
                        id: f.id,
                        nome: f.nome,
                        items: filtro.itens,
                    }
                })

                setFiltros(response)
                sessionStorage.setItem(
                    'preset-filtros',
                    JSON.stringify(response),
                )
            })
        }
    }

    async function create(items: GridFilterModelPro['items'], nome?: string) {
        setLoading(true)
        if (nome && filtros?.find(f => f.nome === nome))
            throw new Error('Nome já está sendo utilizado.')

        const criado = await usuarios.filtros.post({
            nome: nome || undefined,
            items: new FilterModelPro({ items }).toUrlParams(),
        } as any)

        const novosFiltros = [
            ...(filtros || []),
            { id: criado.id, nome: criado.nome, items },
        ]
        setFiltros(novosFiltros)
        sessionStorage.setItem('preset-filtros', JSON.stringify(novosFiltros))
        toast.success('Sua predefinição foi salva com o nome ' + criado.nome)
    }

    async function update(id: number, nome: string) {
        setLoading(true)
        if (!nome) throw new Error('Nome não pode ser vazio.')
        if (filtros?.find(f => f.nome === nome && f.id !== id))
            throw new Error('Nome já está sendo utilizado.')

        await usuarios.filtros.patch(id, {
            nome,
        })

        const filtrosAtualizados = filtros?.map(f =>
            f.id === id ? { id, nome, items: f.items } : f,
        )
        setFiltros(filtrosAtualizados)
        sessionStorage.setItem(
            'preset-filtros',
            JSON.stringify(filtrosAtualizados),
        )
        toast.success('Sua predefinição foi salva com o nome ' + nome)
    }

    async function excluir(id: number) {
        setLoading(true)
        await usuarios.filtros.delete(id)

        const filtrosAtualizados = filtros?.filter(f => f.id !== id)
        setFiltros(filtrosAtualizados)
        sessionStorage.setItem(
            'preset-filtros',
            JSON.stringify(filtrosAtualizados),
        )
        toast.success('Sua predefinição foi excluída')
    }

    function handleError(e: any) {
        console.error(e)
        toast.error(e.message ?? 'Erro ao salvar filtro')
    }

    return {
        filtros: (filtros || []).filter(f =>
            f.items.every(i => columns.find(c => c.field === i.columnField)),
        ),
        adicionarPreset: (itens: GridFilterModelPro['items'], nome?: string) =>
            create(itens, nome)
                .then(() => true)
                .catch(e => {
                    handleError(e)
                    return false
                })
                .finally(() => setLoading(false)),
        atualizarPreset: (id: number, nome: string) =>
            update(id, nome)
                .catch(handleError)
                .finally(() => setLoading(false)),
        excluirPreset: (id: number) =>
            excluir(id)
                .catch(handleError)
                .finally(() => setLoading(false)),
        isLoading,
    }
}
